/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { ticketSurfaces } from '../../shared/ticket/ticketSurfaces.styles';
import { formatMoney } from '../../shared/format/public-api';

type TopPerformer = {
  promotionId: string | null;
  promotionLabel: string;
  usesCount: number;
  discountTotal: number;
};

type Props = {
  topPromotions: TopPerformer[];
};

export function PromotionTopPerformersCard({ topPromotions }: Props) {
  if (topPromotions.length === 0) return null;

  const maxUses = Math.max(...topPromotions.map((p) => p.usesCount), 1);

  return (
    <View style={styles.panel}>
      <View style={styles.header}>
        <Text style={styles.title}>Las que más usaste (30 días)</Text>
        <Text style={styles.hint}>Por usos y plata descontada</Text>
      </View>
      <View style={styles.list}>
        {topPromotions.slice(0, 5).map((promo, idx) => {
          const barWidth = Math.round((promo.usesCount / maxUses) * 100);
          return (
            <View key={promo.promotionId ?? idx} style={styles.row}>
              <View style={styles.rankCol}>
                <Text style={styles.rank}>{idx + 1}</Text>
              </View>
              <View style={styles.infoCol}>
                <Text style={styles.label} numberOfLines={1}>
                  {promo.promotionLabel}
                </Text>
                <View style={styles.barTrack}>
                  <View style={[styles.barFill, { width: `${barWidth}%` }]} />
                </View>
              </View>
              <View style={styles.statsCol}>
                <Text style={styles.uses}>{promo.usesCount} usos</Text>
                <Text style={styles.discount}>
                  {formatMoney(promo.discountTotal)}
                </Text>
              </View>
            </View>
          );
        })}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  panel: {
    ...ticketSurfaces.paperSheet,
    backgroundColor: ticketColors.paper2,
    padding: spacing.md,
    gap: spacing.sm,
  },
  header: {
    gap: 2,
  },
  title: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  hint: {
    fontSize: 11,
    fontWeight: '500',
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
  },
  list: {
    gap: spacing.sm,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  rankCol: {
    width: 22,
  },
  rank: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.ink3,
    fontFamily: ticketFonts.mono,
  },
  infoCol: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  label: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  barTrack: {
    height: 4,
    borderRadius: 2,
    backgroundColor: ticketColors.ruleSoft,
    overflow: 'hidden',
  },
  barFill: {
    height: 4,
    borderRadius: 2,
    backgroundColor: ticketColors.gold,
  },
  statsCol: {
    alignItems: 'flex-end',
    gap: 2,
  },
  uses: {
    fontSize: 11,
    fontWeight: '600',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  discount: {
    fontSize: 12,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.mono,
    fontVariant: ['tabular-nums'],
  },
});
